import { useRef, useEffect } from 'react';
import { SYMBOL_MAP } from '../lib/constants';
import { fmt } from '../lib/formatters';

function buildAssets(positions = []) {
  const assets = [];
  const shown = new Set();

  // Only show symbols with open positions
  for (const pos of positions) {
    if (!shown.has(pos.symbol) && SYMBOL_MAP[pos.symbol]) {
      const name = pos.symbol.replace('USD', '');
      const price = pos.entryPrice || 0;
      const decimals = price > 100 ? 2 : price > 1 ? 4 : 6;
      assets.push({ name, symbol: pos.symbol, decimals, minWidth: '120px' });
      shown.add(pos.symbol);
    }
  }

  return assets.map(a => ({ ...a, symbolId: SYMBOL_MAP[a.symbol] }));
}

function PriceCell({ name, price, decimals, minWidth, position, onClick }) {
  const ref = useRef(null);
  const prevPrice = useRef(price);

  useEffect(() => {
    if (price !== prevPrice.current && ref.current) {
      ref.current.classList.add('text-warning');
      const timer = setTimeout(() => ref.current?.classList.remove('text-warning'), 300);
      prevPrice.current = price;
      return () => clearTimeout(timer);
    }
  }, [price]);

  const side = position?.side;
  const sideLabel = side === 'buy' ? 'LONG' : side === 'sell' ? 'SHORT' : null;
  const sideColor = side === 'buy' ? 'text-success' : side === 'sell' ? 'text-error' : '';

  return (
    <div
      className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2 cursor-pointer hover:bg-base-300 transition-colors"
      style={{ minWidth }}
      onClick={onClick}
      title={`Click to view ${name} chart`}
    >
      <div className="flex flex-col items-start">
        <span className="text-xs opacity-60 font-mono">{name}</span>
        {sideLabel && (
          <span className={`text-[10px] font-bold ${sideColor}`}>{sideLabel}</span>
        )}
      </div>
      <span ref={ref} className="font-mono font-bold text-lg transition-colors">
        {price ? fmt(price, decimals) : '-'}
      </span>
      <i className="fa-solid fa-chart-line text-xs opacity-30"></i>
    </div>
  );
}

export default function PriceTicker({ prices, positions = [], onSymbolClick }) {
  // Map positions by symbol for quick lookup
  const positionBySymbol = {};
  for (const p of positions) {
    positionBySymbol[p.symbol] = p;
  }

  const ASSETS = buildAssets(positions);

  return (
    <div className="flex gap-3 flex-wrap">
      {ASSETS.map(({ name, symbol, symbolId, decimals, minWidth }) => (
        <PriceCell
          key={name}
          name={name}
          price={prices[symbolId]?.bid}
          decimals={decimals}
          minWidth={minWidth}
          position={positionBySymbol[symbol]}
          onClick={() => onSymbolClick?.(symbol)}
        />
      ))}
    </div>
  );
}
